import { createTheme, Theme } from "@mui/material/styles";

/**
 * Custom theme extension for Billet Yard Inventory module
 * Provides consistent colors and avoids hardcoded values
 */

// Extend MUI theme with custom palette
declare module "@mui/material/styles" {
  interface Palette {
    billetYard: {
      selected: {
        background: string;
        border: string;
        hover: string;
      };
      loginSelected: {
        background: string;
        border: string;
        hover: string;
        shadow: string;
      };
      borders: {
        light: string;
        medium: string;
        dark: string;
      };
      backgrounds: {
        light: string;
        lighter: string;
        gradient: string;
      };
      searchHighlight: string;
      heatStatus: {
        fail: { background: string; text: string };
        chemistry: { background: string; text: string };
        tags: { background: string; text: string };
        both: { background: string; text: string };
      };
    };
  }

  interface PaletteOptions {
    billetYard?: {
      selected?: {
        background?: string;
        border?: string;
        hover?: string;
      };
      loginSelected?: {
        background?: string;
        border?: string;
        hover?: string;
        shadow?: string;
      };
      borders?: {
        light?: string;
        medium?: string;
        dark?: string;
      };
      backgrounds?: {
        light?: string;
        lighter?: string;
        gradient?: string;
      };
      searchHighlight?: string;
      heatStatus?: {
        fail?: { background?: string; text?: string };
        chemistry?: { background?: string; text?: string };
        tags?: { background?: string; text?: string };
        both?: { background?: string; text?: string };
      };
    };
  }
}

/**
 * Create theme extension with billet yard specific colors
 * This can be merged with the global MUI theme
 *
 * NOTE: Use standard MUI palette for common colors:
 * - Primary colors: theme.palette.primary.main/dark/light (#3f51b5)
 * - Error colors: theme.palette.error.main (#f44336)
 * - Warning colors: theme.palette.warning.main (#ff9800)
 * - Info colors: theme.palette.info.main (#2196f3)
 * - Text colors: theme.palette.text.primary/secondary
 * - Background colors: theme.palette.background.paper/default
 * - Grey colors: theme.palette.grey[100-900]
 *
 * Only custom colors specific to Billet Yard are defined here.
 */
export const billetYardThemeExtension = {
  palette: {
    billetYard: {
      // Selected state colors (yellow/amber highlight) - matches develop branch
      // Uses warning palette colors to match theme.palette.warning.light and warning.main
      selected: {
        background: "#fff3cd", // theme.palette.warning.light - yellow/amber background
        border: "#ffc107", // theme.palette.warning.main - darker warning color for border
        hover: "#ffc107", // theme.palette.warning.main - matches border on hover (from develop)
      },

      // Login selection colors (blue) - for crane/operator selection modal
      // Blue provides better visibility on outdoor touch screens in sunlight
      loginSelected: {
        background: "#bbdefb", // Light blue background
        border: "#1976d2", // Primary blue border
        hover: "#90caf9", // Lighter blue on hover
        shadow: "rgba(33, 150, 243, 0.3)", // Blue shadow
      },

      // Border colors - Custom light borders
      borders: {
        light: "#e5e7eb",
        medium: "#e0e0e0",
        dark: "#ddd",
      },

      // Background colors - Custom gradients and light backgrounds
      backgrounds: {
        light: "#f5f5f5",
        lighter: "#f8fafc",
        gradient: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
      },

      // Search highlight color - blue tones for search results
      searchHighlight: "#468BE6", // Darker blue for borders and billet item backgrounds

      // Heat status colors - for AxiomHeatStatus chips
      heatStatus: {
        fail: { background: "#d32f2f", text: "#ffffff" }, // Dark red with white text
        chemistry: { background: "#bbdefb", text: "#1565c0" }, // Light blue
        tags: { background: "#ffcc80", text: "#e65100" }, // Orange
        both: { background: "#e1bee7", text: "#7b1fa2" }, // Purple
      },
    },
  },
};

/**
 * Create a complete theme with billet yard extensions
 * Use this if you need a standalone theme
 */
export const createBilletYardTheme = (baseTheme?: Theme): Theme => {
  return createTheme({
    ...baseTheme,
    palette: {
      ...baseTheme?.palette,
      ...billetYardThemeExtension.palette,
    },
  });
};

/**
 * Helper function to get billet yard colors from any theme
 * Usage: getBilletYardColors(theme).selected.background
 */
export const getBilletYardColors = (theme: Theme) => {
  return theme.palette.billetYard;
};
